import { SwipeableDrawer } from '@mui/material';
import { t } from 'i18next';
import { memo, useEffect } from 'react';
import BackButton from '../../components/Buttons/BackButton';
import useDrawerOpenClose from '../../components/Modals/Drawers/hooks/useDrawerOpenClose';
import {
  DRAWER_TRANSITION,
  DRAWER_TRANSITION_DURATION,
} from '../../components/Modals/Drawers/utils';
import {
  drawerFullScreenWithTopBarContentStyles,
  drawerPaperFullScreenStyles,
  modalPaperRootBackdropStyles,
  modalTopBarStyles,
} from '../../components/Modals/utils';
import TopMenuPageTitle from '../../components/TopMenuBar.tsx/TopMenuPageTitle';
import ProfileImage from '../../components/ProfileImage';
import { useGetBlockedUsers } from '../../graphql/apis/users/queries/hooks/useGetBlockedUsers';
import UnblockActionButton from '../../components/User/UnblockActionButton';
import { colors } from '../../theme/colors';
import spacing from '../../theme/spacing';
import { fontSize } from '../../theme/typography';
import { useRegisterPriorityScrollableContentRef } from '../RootPage/contexts/PriorityScrollableContentProvider/hooks/useRegisterPriorityScrollableContentRef';
import withPriorityScrollableContentProvider from '../RootPage/contexts/PriorityScrollableContentProvider/hooks/withPriorityScrollableContentProvider';

interface BlockedUsersModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

interface BlockedUser {
  _id: string;
  username: string;
  name: string;
  profileImage?: string;
}

const TopBar = ({ onBackClick }: { onBackClick: () => void }) => {
  return (
    <div style={modalTopBarStyles}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <BackButton
          onClick={onBackClick}
          style={{
            paddingLeft: spacing.medium,
            paddingRight: spacing.medium,
          }}
        />
        <TopMenuPageTitle title={t('accountSettings.blockedUsers')} />
      </div>

      <div
        style={{
          display: 'inline-block',
        }}
      />
    </div>
  );
};

const BlockedUser = ({
  user,
  refetchBlockedUsers,
}: {
  user: BlockedUser;
  refetchBlockedUsers: () => Promise<any>;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: spacing.medium,
        paddingBottom: spacing.medium,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <ProfileImage
          src={user.profileImage}
          username={user.username}
          size={40}
        />
        <div style={{ marginLeft: spacing.medium }}>
          <div
            style={{
              fontSize: fontSize.regular,
              fontWeight: '500',
              color: colors.textPrimary,
            }}
          >
            {user.name}
          </div>
          <div
            style={{
              fontSize: fontSize.sm,
              color: colors.textGreyMedium,
            }}
          >
            @{user.username}
          </div>
        </div>
      </div>
      <UnblockActionButton
        user={user}
        initialIsBlocked={true}
        refetchBlockedUsers={refetchBlockedUsers}
      />
    </div>
  );
};

const BlockedUsersModal = memo(
  ({ isOpen, handleClose }: BlockedUsersModalProps) => {
    const { blockedUsers, loading, refetch } = useGetBlockedUsers();

    const scrollableRef = useRegisterPriorityScrollableContentRef();

    const { isDrawerOpen, setDrawerToClose, drawerOpacity } =
      useDrawerOpenClose({
        isOpen,
        handleManualClose: handleClose,
      });

    useEffect(() => {
      scrollableRef?.current?.scrollTo({
        top: 0,
      });
    }, []);

    return (
      <SwipeableDrawer
        onClose={setDrawerToClose}
        open={isDrawerOpen}
        anchor="right"
        onOpen={() => console.log('')}
        transitionDuration={DRAWER_TRANSITION_DURATION}
        disableBackdropTransition
        sx={{
          opacity: drawerOpacity,
          transition: DRAWER_TRANSITION,
          '& .MuiPaper-root': {
            ...drawerPaperFullScreenStyles,
          },
          '& .MuiBackdrop-root': {
            ...modalPaperRootBackdropStyles,
            pointerEvents: isDrawerOpen ? 'auto' : 'none',
          },
        }}
      >
        <TopBar onBackClick={setDrawerToClose} />

        <div
          ref={scrollableRef}
          style={drawerFullScreenWithTopBarContentStyles}
        >
          <div
            style={{
              paddingLeft: spacing.medium,
              paddingRight: spacing.medium,
              marginTop: spacing.medium,
            }}
          >
            {loading ? (
              <div
                style={{
                  padding: spacing.large,
                  textAlign: 'center',
                  color: colors.textGreyMedium,
                }}
              >
                {t('common.loading')}...
              </div>
            ) : blockedUsers.length === 0 ? (
              <div
                style={{
                  padding: spacing.large,
                  textAlign: 'center',
                  color: colors.textGreyMedium,
                }}
              >
                {t('accountSettings.noBlockedUsers')}
              </div>
            ) : (
              <div>
                {blockedUsers.map((user: BlockedUser) => (
                  <BlockedUser
                    key={user._id}
                    user={user}
                    refetchBlockedUsers={refetch}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    );
  },
);

export default withPriorityScrollableContentProvider(BlockedUsersModal);
